import React, { useEffect, useState, useMemo, useCallback } from "react";
import { Container, Row, Col } from "react-bootstrap";
import { Helmet } from "react-helmet-async";
import Select from "react-select";
import Table from "../../../components/table/Table";
import { getLeaderBoardFilters } from "../../../services/api/queries/users";
import leaderboardListViewMeta from "../../../configs/meta/listViewMeta/leaderBoardViewMeta";
import useAuth from "../../../hooks/useAuth";

const LeaderBoard = () => {
  const { user } = useAuth();
  const stableParams = useMemo(() => new URLSearchParams(), []);
  const [courses, setCourses] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [filtersLoaded, setFiltersLoaded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [tableData, setTableData] = useState({ rowData: [], total: 0 });

  const meta = useMemo(
    () =>
      leaderboardListViewMeta({
        topic_id: selectedTopic?.value || "",
      }),
    [selectedCourse?.value, selectedTopic?.value]
  );

  const { columns, queryService } = meta;

  useEffect(() => {
    getLeaderBoardFilters()
      .then((res) => {
        setCourses(res?.data || []);
        setFiltersLoaded(true);
      })
      .catch(() => {
        setCourses([]);
        setFiltersLoaded(true);
      });
  }, []);

  const courseOptions = useMemo(
    () => courses.map((c) => ({ label: c.name, value: c.id })),
    [courses]
  );

  const topicOptions = useMemo(() => {
    if (!selectedCourse) return [];
    const course = courses.find((c) => c.id === selectedCourse.value);
    return (course?.topics || []).map((t) => ({
      label: t.name,
      value: t.template_id,
    }));
  }, [courses, selectedCourse]);

  const handleCourseChange = (option) => {
    setSelectedCourse(option);
    setSelectedTopic(null);
    setTableData({ rowData: [], total: 0 });
  };

  const handleTopicChange = (option) => {
    setSelectedTopic(option);
  };

  const fetchLeaderboard = useCallback(() => {
    const courseId = selectedCourse?.value;
    const topicId = selectedTopic?.value;
    if (!courseId || !topicId) return;
    setIsLoading(true);
    queryService({ course_id: courseId, topic_id: topicId })
      .then((info) => {
        const { results } = info.data;
        setTableData({
          rowData: results || [],
          total: results?.length || 0,
        });
      })
      .catch(() => {
        setTableData({ rowData: [], total: 0 });
      })
      .finally(() => setIsLoading(false));
  }, [queryService, selectedCourse?.value, selectedTopic?.value]);

  const currentUserData = useMemo(() => {
    if (!user?.id || !tableData.rowData.length) return null;
    const index = tableData.rowData.findIndex(
      (r) => String(r.user_id) === String(user.id)
    );
    if (index === -1) return null;
    return { position: index + 1, data: tableData.rowData[index] };
  }, [tableData.rowData, user?.id]);

  const showTable = filtersLoaded && selectedCourse && selectedTopic;

  return (
    <>
      <Helmet title="LeaderBoard" />
      <Container fluid className="p-0">
        <h1 className="h3 mb-3">Leaderboard</h1>

        <Row className="mb-3 gap-2">
          <Col md={3}>
            <Select
              options={courseOptions}
              value={selectedCourse}
              onChange={handleCourseChange}
              placeholder="Select Course"
              isClearable
            />
          </Col>
          <Col md={3}>
            <Select
              options={topicOptions}
              value={selectedTopic}
              onChange={handleTopicChange}
              placeholder="Select Topic"
              isClearable
              isDisabled={!selectedCourse}
            />
          </Col>
        </Row>

        {showTable && (
          <Row className="mb-3 gap-2">
            <Col md={3}>
              <div className="p-3 bg-white rounded shadow-sm text-center">
                <div className="text-muted small">Your Position</div>
                <div className="h3 mb-0 fw-bold">
                  {currentUserData?.position
                    ? `#${currentUserData.position}`
                    : "Not Participated"}
                </div>
              </div>
            </Col>
            <Col md={3}>
              <div className="p-3 bg-white rounded shadow-sm text-center">
                <div className="text-muted small">Total Participants</div>
                <div className="h3 mb-0 fw-bold">{tableData.total}</div>
              </div>
            </Col>
          </Row>
        )}

        {showTable ? (
          <Table
            tableData={tableData}
            setTableData={setTableData}
            isLoading={isLoading}
            title="User Leaderboard"
            columns={columns}
            performQuery={fetchLeaderboard}
            actions={[]}
            data={[]}
            setIsLoading={setIsLoading}
            pagination={false}
            params={stableParams}
            setParams={() => {}}
          />
        ) : (
          <p className="text-muted">
            Please select both Course and Topic to view leaderboard.
          </p>
        )}
      </Container>
    </>
  );
};

export default LeaderBoard;
